// Text for the Help page (pages/Help.tsx). One topic per screen, grouped
// into the page's tabs. Edit the words here; the page only lays them out.
// Copy rules (CLAUDE.md "On-screen text"): simple short English, no dashes
// joining sentences, inventory things are "items".

export type HelpTabKey = 'selling' | 'buying' | 'stock' | 'money' | 'companies' | 'accounting' | 'gst';

export const HELP_TABS: { key: HelpTabKey; label: string }[] = [
  { key: 'selling', label: 'Selling' },
  { key: 'buying',  label: 'Buying' },
  { key: 'stock',   label: 'Stock' },
  { key: 'money',   label: 'Money' },
  { key: 'companies', label: 'Companies' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'gst',     label: 'GST' },
];

export interface HelpSection {
  title: string;
  /** Numbered steps, in order. */
  steps?: string[];
  /** Plain points, no order. */
  points?: string[];
}

export interface HelpTopic {
  key: string;
  tab: HelpTabKey;
  title: string;
  /** The page it explains: a PAGE_GROUPS key (who may see it) and its path. */
  page: string;
  path: string;
  /** One line: what it is for. */
  intro: string;
  /** Only for companies on the Indian trading system (GST, Less 1 / 2). */
  indianOnly?: boolean;
  sections: HelpSection[];
}

export const HELP_TOPICS: HelpTopic[] = [
  // ── Selling ────────────────────────────────────────────────────────────
  {
    key: 'sales', tab: 'selling', title: 'Sales', page: 'sales', path: '/sales',
    intro: 'Make a sale invoice for a customer. Stock, Receivable and the books update by themselves.',
    sections: [
      {
        title: 'Add a sale',
        steps: [
          'Open Sales and click Add Sale.',
          'Pick the customer in To (Customer). Type to search. Use + Create New Entity if they are new.',
          'Check the Sale Date and Payment Terms. Keep(%) fills in from the customer and can be changed.',
          'Type an item Code in the last row and press Enter to add it. Or click in the Code box and pick from the list.',
          'Set the Carat to sell (part of a parcel is fine) and the Price/ct.',
          'Add shipping or other costs under Additional Charges & Expenses if needed. Choose who pays: Our cost or Charge customer.',
          'Click Save Invoice.',
        ],
      },
      {
        title: 'Sell items the customer has on memo',
        steps: [
          'In Add Sale, pick the customer first.',
          'Their open memos show under On Memo with This Customer.',
          'Click Add to Sale on each one you want to sell. The memo closes when you save.',
        ],
      },
      {
        title: 'Edit a sale',
        steps: [
          'Click the sale in the list to open it.',
          'Click Edit, change what you need and save.',
        ],
        points: ['A sale that is fully paid can\'t be edited.'],
      },
      {
        title: 'Delete a sale',
        steps: ['Open the sale and click Delete Invoice, then confirm.'],
        points: ['The items go back into stock and the books are reversed.'],
      },
      {
        title: 'Good to know',
        points: [
          'Red cells show what is still missing. The count shows as "N to fix" in the totals bar.',
          'Open a sale to print it or download it as PDF. Mark as Sent once you send it.',
          'For a return, use Sales Returns / Credit Notes. Don\'t edit the sale.',
        ],
      },
    ],
  },
  {
    key: 'memo-out', tab: 'selling', title: 'Memo Out', page: 'memos', path: '/memos',
    intro: 'Give items to a customer on approval. You still own them until they are sold.',
    sections: [
      {
        title: 'Send items on memo',
        steps: [
          'Open Memo Out and click Send On Memo. You can also do it from an item\'s history in Inventory.',
          'Pick the customer and the item.',
          'Set the carat to send. Part of a parcel is fine: the rest stays free to sell.',
          'Check the Price/ct and the Expected Return Date, then save.',
        ],
      },
      {
        title: 'When the items come back',
        steps: [
          'Find the memo in the list and open its ⋮ menu.',
          'Click Return, enter the carat and date, then save.',
        ],
      },
      {
        title: 'When the customer buys them',
        points: ['Make a normal sale for that customer and click Add to Sale under On Memo with This Customer. See Sales.'],
      },
      {
        title: 'Change or delete a memo',
        points: [
          'Extend (⋮ menu) moves the return date.',
          'Delete (⋮ menu) removes a memo added by mistake. Only for memos still out, not returned or sold.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'Switch between By Item and Consignments at the top.',
          'Late memos show as Overdue in the Return By column.',
        ],
      },
    ],
  },

  // ── Buying ─────────────────────────────────────────────────────────────
  {
    key: 'purchase', tab: 'buying', title: 'Purchase', page: 'purchase', path: '/purchase',
    intro: 'Enter what you bought from a supplier. The items go into stock and the amount goes to Payable.',
    sections: [
      {
        title: 'Add a purchase invoice',
        steps: [
          'Open Purchase and click Add Invoice.',
          'Pick the supplier in Company Name. Fill Supplier Inv. No. and Purchase Date.',
          'Set the Payment Terms. The Due Date works itself out.',
          'Choose the Loose or Certified tab and fill one row per item. The Code fills in by itself.',
          'Loose items need an SKU. Certified items need a Lab and Cert No.',
          'Enter the Cost/ct (what you pay). Added Value % gives the Price/ct, your stock figure.',
          'Use Add Loose Row or Add Certified Row for more items, then click Save Invoice.',
        ],
      },
      {
        title: 'Import from Excel',
        steps: [
          'Click the Import icon in the header and download the sample file.',
          'Fill it in Excel. One file can hold many invoices.',
          'Drop the file in, check the preview (red cells must be fixed) and import.',
        ],
      },
      {
        title: 'Edit a purchase',
        steps: ['Click the invoice in the list to open it.', 'Click Edit, change what you need and save.'],
      },
      {
        title: 'Delete a purchase',
        steps: ['Open the invoice and click Delete Invoice, then confirm.'],
        points: ['A purchase with a supplier discount can\'t be deleted until the discount is removed.'],
      },
      {
        title: 'Good to know',
        points: [
          'Cost/ct is what you paid. Price/ct is Cost/ct plus Added Value %.',
          'To send items back to the supplier, use Purchase Returns.',
          'Pay the supplier from the invoice or from Payable.',
        ],
      },
    ],
  },
  {
    key: 'memo-in', tab: 'buying', title: 'Memo In', page: 'memo-in', path: '/purchase/memo-in',
    intro: 'Items a supplier gave you on approval. They are not yours until you convert them to a purchase.',
    sections: [
      {
        title: 'Receive a consignment',
        steps: [
          'Open Memo In and click Receive Consignment.',
          'Pick the supplier, then fill Their Memo / Ref No., Received Date and Return By.',
          'Add one row per item with the supplier\'s Price/ct, then save.',
        ],
      },
      {
        title: 'Keep an item (buy it)',
        steps: [
          'Open the consignment and click Convert to Purchase on the line.',
          'Fill the purchase details. The supplier\'s Price/ct becomes your Cost/ct.',
          'Save. The item now has a Code and is in stock.',
        ],
      },
      {
        title: 'Give an item back',
        steps: ['Open the consignment and click Return on the line.', 'Enter the date and click Return Stone.'],
      },
      {
        title: 'Delete a consignment',
        points: ['Open it and click Delete Consignment. Only while every item is still held.'],
      },
      {
        title: 'Good to know',
        points: ['You can also import consignments from Excel with the Import icon.'],
      },
    ],
  },

  // ── Stock ──────────────────────────────────────────────────────────────
  {
    key: 'inventory', tab: 'stock', title: 'Inventory', page: 'inventory', path: '/inventory',
    intro: 'Every item you have in stock, with its cost, price and history.',
    sections: [
      {
        title: 'Find an item',
        points: [
          'Search by Code, SKU or cert number.',
          'Use All, Loose or Certified at the top.',
          'Click the funnel in a column heading to filter, for example by Color or Shape.',
        ],
      },
      {
        title: 'See an item\'s history',
        steps: [
          'Click the item in the list.',
          'Item History shows every purchase, memo, sale, return and revaluation for it, newest first.',
        ],
      },
      {
        title: 'Act on an item',
        points: [
          'From Item History: Send On Memo, Memo In (record a memo coming back), Add Sale, Revalue, Adjust and Split & Merge.',
          'These open the right page with the item already picked.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'Carat shows what is in hand. Carat out on memo shows in orange under it.',
          'Total Cost and Total Price count the in hand part only.',
          'A GIA, IGI or HRD cert number opens the lab\'s report.',
          'Columns lets you hide columns. Export gives a CSV or a printed list.',
        ],
      },
    ],
  },

  // ── Money ──────────────────────────────────────────────────────────────
  {
    key: 'receivable', tab: 'money', title: 'Receivable', page: 'ar', path: '/ar',
    intro: 'Money customers still owe you, invoice by invoice.',
    sections: [
      {
        title: 'Record money received',
        steps: [
          'Open Receivable and click the invoice.',
          'Click Record Payment.',
          'Pick the bank account, enter the amount and date, then save.',
        ],
        points: ['A payment can also be recorded from Bank › New › Payment received.'],
      },
      {
        title: 'Change or delete a payment',
        steps: ['Open the invoice and go to the Payments tab.', 'Use Edit or Delete on the payment.'],
      },
      {
        title: 'Good to know',
        points: [
          'The list starts on Unpaid and Partly paid. Use the Status funnel to see paid invoices.',
          'Calendar view shows what is due on each day.',
          'A small leftover (bank fee, rounding) can be closed with Settle difference on the Payments tab.',
        ],
      },
    ],
  },
  {
    key: 'payable', tab: 'money', title: 'Payable', page: 'ap', path: '/ap',
    intro: 'Money you still owe suppliers, invoice by invoice.',
    sections: [
      {
        title: 'Pay a supplier',
        steps: [
          'Open Payable and click the invoice.',
          'Click Record Payment.',
          'Pick the bank account, enter the amount and date, then save.',
        ],
        points: ['A payment can also be recorded from Bank › New › Payment made.'],
      },
      {
        title: 'Change or delete a payment',
        steps: ['Open the invoice and find the payment under Payment History.', 'Click Edit or Delete.'],
      },
      {
        title: 'Good to know',
        points: [
          'The list starts on Unpaid and Partly paid. Use the Status funnel to see paid invoices.',
          'A supplier discount is added on the purchase invoice, under Discounts.',
          'Calendar view shows what is due on each day.',
        ],
      },
    ],
  },
  {
    key: 'bank', tab: 'money', title: 'Bank', page: 'bank', path: '/bank',
    intro: 'Your bank and cash accounts, with every amount that came in or went out.',
    sections: [
      {
        title: 'Add a bank or cash account',
        steps: [
          'Open Bank and switch to Accounts at the top.',
          'Click Add Account.',
          'Choose Bank or Cash, fill the name, currency and opening balance, then save.',
        ],
      },
      {
        title: 'Record money in or out',
        steps: ['On Transactions, click New and pick what happened:'],
        points: [
          'Payment received (customer): a customer paid you. Pick the customer, then the invoice it pays.',
          'Payment made (supplier): you paid a supplier. Pick the supplier, then the invoice.',
          'Expense or Income: money not linked to an invoice, like rent.',
          'Transfer between accounts, Deposit cash, Withdraw cash or Bank charge.',
        ],
      },
      {
        title: 'Find a transaction',
        points: [
          'Use the arrows to move a month. Click the month to pick one day.',
          'Click an account card at the top to see only that account. Click it again to see all.',
          'Click the funnel in a column heading to filter by Type, Party and more.',
        ],
      },
      {
        title: 'Change or delete',
        points: [
          'Transfers, deposits, withdrawals, bank charges, expenses and income can be deleted with the red bin.',
          'A customer or supplier payment is changed or deleted from its invoice, so the invoice stays right.',
          'Edit or Deactivate an account from its card on the Accounts tab.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'Balances are shown per currency. Different currencies are never added together.',
          'An invoice number in the list opens that invoice.',
        ],
      },
    ],
  },
  {
    key: 'brokers', tab: 'money', title: 'Brokers', page: 'brokers', path: '/brokers',
    intro: 'Commission you owe brokers on sales and purchases, and paying it.',
    sections: [
      {
        title: 'Give a broker commission',
        steps: [
          'In Add Sale or Add Invoice (purchase), pick the broker in Broker1 on a line.',
          'Enter the Broker1% for that line. Use Broker2 when a second broker shares the deal.',
          'Save the invoice. The commission shows on the Brokers page.',
        ],
        points: ['Commission is worked out on the full price, before Keep(%).'],
      },
      {
        title: 'Pay a broker',
        steps: [
          'Open Brokers and click the invoice row.',
          'For a sale this opens the sale on its Brokers tab. Click Pay on the broker.',
          'Pick the bank account, check the amount and date, then save.',
        ],
      },
      {
        title: 'Pay many at once',
        steps: [
          'Tick the sale rows you want to pay.',
          'Click Pay Selected, check the amounts and the bank account, then pay.',
        ],
        points: ['Pay Selected works for sales. Purchase rows are paid one at a time.'],
      },
      {
        title: 'Change or delete a payout',
        points: ['Open the sale, go to its Brokers tab and use Edit or Delete under the payout.'],
      },
      {
        title: 'Good to know',
        points: [
          'The list starts on Unpaid and Partly paid. Use the Status funnel to see paid ones.',
          '1M, 3M, 6M, 1Y and 2Y show everything overdue plus what is due in that time. Custom picks the dates.',
          'Filter by broker with the funnel on the Brokers column.',
        ],
      },
    ],
  },

  // ── Selling (more) ─────────────────────────────────────────────────────
  {
    key: 'sales-returns', tab: 'selling', title: 'Sales Returns', page: 'sales-returns', path: '/sales/returns',
    intro: 'When a customer sends items back. A credit note lowers what they owe you.',
    sections: [
      {
        title: 'Make a credit note',
        steps: [
          'Open Sales Returns / Credit Notes and click New Credit Note.',
          'Pick the sale the items came from.',
          'Enter the Return ct for each item coming back.',
          'Check the Price/ct. It starts at what the customer paid. Lower it to credit less, for example for a damaged item.',
          'Add a Return Reason and click Save.',
        ],
      },
      {
        title: 'Delete a credit note',
        points: ['Use the red bin on its row. The items leave stock again and the credit is taken off.'],
      },
      {
        title: 'Good to know',
        points: [
          'Items cut from a parcel go back into that same parcel when it is still in stock.',
          'Back at /ct shows the Price/ct the items go back into stock at.',
        ],
      },
    ],
  },
  {
    key: 'sales-expenses', tab: 'selling', title: 'Sales Expenses', page: 'sales-expenses', path: '/sales/expenses',
    intro: 'Shipping, insurance and other costs on a sale.',
    sections: [
      {
        title: 'Add an expense',
        steps: [
          'Click Add Expense, or add it inside the sale under Additional Charges & Expenses.',
          'Pick the sale, the category, the date and the amount.',
          'Choose Who pays: Our cost (lowers your profit) or Charge customer (added to the invoice).',
          'Choose the account if paid now, or a due date if you pay later. Save.',
        ],
      },
      {
        title: 'Pay, change or remove',
        points: [
          'Pay on the row pays an unpaid expense.',
          'Change or remove an expense in the sale\'s own form.',
        ],
      },
    ],
  },
  {
    key: 'customer-advances', tab: 'selling', title: 'Customer Advances', page: 'customer-advances', path: '/sales/advances',
    intro: 'Money a customer paid you before there was an invoice.',
    sections: [
      {
        title: 'Record an advance',
        steps: ['Click New Advance.', 'Pick the customer, the bank account, the amount and the date. Save.'],
      },
      {
        title: 'Use it on invoices',
        steps: [
          'Click Apply on the advance.',
          'Fill the amounts per invoice. Oldest first fills by due date, Split equally shares it out, Full pays one invoice.',
          'Click Apply to save.',
        ],
      },
      {
        title: 'Undo or delete',
        points: [
          'Open the advance\'s row to see where it was used. Un-apply takes one use back.',
          'Delete an advance with the red bin.',
        ],
      },
    ],
  },

  // ── Buying (more) ──────────────────────────────────────────────────────
  {
    key: 'purchase-returns', tab: 'buying', title: 'Purchase Returns', page: 'purchase-returns', path: '/purchase/returns',
    intro: 'When you send items back to a supplier. It lowers what you owe them.',
    sections: [
      {
        title: 'Make a return',
        steps: [
          'Open Purchase Returns and click New Return.',
          'Pick the purchase the items came from.',
          'Enter the carat going back for each item, then save.',
        ],
      },
      {
        title: 'Delete a return',
        points: ['Use the red bin on its row. The items come back into stock.'],
      },
      {
        title: 'Good to know',
        points: [
          'Carat out on memo can\'t be returned. Only the free part can.',
          'An item returned in full leaves the stock list.',
        ],
      },
    ],
  },
  {
    key: 'purchase-expenses', tab: 'buying', title: 'Purchase Expenses', page: 'purchase-expenses', path: '/purchase/expenses',
    intro: 'Costs of buying, like shipping, customs or lab fees.',
    sections: [
      {
        title: 'Add an expense',
        steps: [
          'Click Add Expense and pick the purchase it belongs to.',
          'Fill the category, date, description and amount.',
          'Choose the Treatment: Capitalise into stock cost (adds to the items\' cost) or Expense now (to Profit).',
          'Choose Paid from (an account) or a due date if you pay later. Save.',
        ],
      },
      {
        title: 'Pay or delete',
        points: ['Pay on the row pays an unpaid expense. Delete with the red bin.'],
      },
    ],
  },
  {
    key: 'supplier-advances', tab: 'buying', title: 'Supplier Advances', page: 'supplier-advances', path: '/purchase/advances',
    intro: 'Money you paid a supplier before there was an invoice.',
    sections: [
      {
        title: 'Record an advance',
        steps: ['Click New Advance.', 'Pick the supplier, the bank account, the amount and the date. Save.'],
      },
      {
        title: 'Use it on invoices',
        steps: [
          'Click Apply on the advance.',
          'Fill the amounts per invoice, or use Oldest first, Split equally or Full.',
          'Click Apply to save.',
        ],
      },
      {
        title: 'Undo or delete',
        points: ['Un-apply takes one use back. Delete an advance with the red bin.'],
      },
    ],
  },

  // ── Stock (more) ───────────────────────────────────────────────────────
  {
    key: 'opening-stock', tab: 'stock', title: 'Opening Stock', page: 'opening-stock', path: '/inventory/opening',
    intro: 'Items you already own when you start using the app.',
    sections: [
      {
        title: 'Load opening stock',
        steps: [
          'Open Inventory › Opening Stock.',
          'Set the Stock date and an optional note at the top.',
          'Fill the Loose and Certified tabs, one row per item. Use Add Row for more.',
          'Or click the Import icon and load an Excel file.',
          'Fix any red cells, then click Load Opening Stock.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'Opening stock has no supplier and no invoice. After loading, the items live in Inventory.',
          'Each Code must be different.',
        ],
      },
    ],
  },
  {
    key: 'split-merge', tab: 'stock', title: 'Split & Merge', page: 'inventory-merge', path: '/inventory/merge',
    intro: 'Cut part of a loose parcel off and move it into another Code, new or existing.',
    sections: [
      {
        title: 'Split or merge',
        steps: [
          'Open Inventory › Split & Merge and pick the From parcel.',
          'Enter the carat and Price/ct of the part you cut off.',
          'Type the destination Code. A Code that exists is added to. A new Code makes a new parcel.',
          'Click Add Cut. Add more cuts if needed.',
          'Check what is left on the parcel, then click Save.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'Nothing is saved until you click Save, so you can remove a cut first.',
          'History in the header shows past splits.',
          'Only free carat can be cut. Carat out on memo stays.',
        ],
      },
    ],
  },
  {
    key: 'stock-adjustment', tab: 'stock', title: 'Stock Adjustment', page: 'stock-adjustment', path: '/inventory/adjustment',
    intro: 'Fix the carat or value of an item after a loss, a gain or a write off.',
    sections: [
      {
        title: 'Adjust an item',
        steps: [
          'Click Adjust Stock and pick the item.',
          'Choose the Type: Loss, Gain or Write off.',
          'Enter the carat change and the reason, then click Save Adjustment.',
        ],
      },
      {
        title: 'Delete',
        points: ['Use the red bin on the row to undo an adjustment.'],
      },
    ],
  },
  {
    key: 'stock-revaluation', tab: 'stock', title: 'Stock Revaluation', page: 'stock-revaluation', path: '/inventory/revaluation',
    intro: 'Change the cost per ct of items, for example when prices move.',
    sections: [
      {
        title: 'Revalue one item',
        steps: [
          'Click Revalue Stock and pick the item.',
          'Enter the new cost per ct and a reason, then click Save Revaluation.',
        ],
      },
      {
        title: 'Revalue many items',
        steps: [
          'Click Revalue Many.',
          'Type a New Cost/ct or a % Change on each row. Or tick rows and apply one % to all of them.',
          'Click Save. Only the rows you changed are saved.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'A revaluation covers the whole item, carat on memo too.',
          'The gain or loss shows in Profit & Loss.',
          'Deleting a row from a Revalue Many run asks: This item only, or the whole batch.',
        ],
      },
    ],
  },
  {
    key: 'stock-transfer', tab: 'stock', title: 'Stock Transfer', page: 'stock-transfer', path: '/inventory/transfer',
    intro: 'Move items from one location to another, for example office to safe.',
    sections: [
      {
        title: 'Move an item',
        steps: [
          'Click Move Stock and pick the item.',
          'Enter the carat to move and the To location, then click Move.',
        ],
      },
    ],
  },

  // ── Money (more) ───────────────────────────────────────────────────────
  {
    key: 'bank-reconcile', tab: 'money', title: 'Bank Reconciliation', page: 'bank-reconcile', path: '/bank/reconcile',
    intro: 'Check your account against the bank statement, line by line.',
    sections: [
      {
        title: 'Reconcile',
        steps: [
          'Open Bank Reconciliation and pick the account.',
          'Enter the Statement date and the Opening and Closing balance from the statement.',
          'Tick every line that is on the statement.',
          'Missing a fee? Use Add bank charge.',
          'When the difference is zero, click Reconcile.',
        ],
      },
    ],
  },

  // ── Companies ──────────────────────────────────────────────────────────
  {
    key: 'entity', tab: 'companies', title: 'Entity', page: 'entity', path: '/entity',
    intro: 'Every company you work with: customers, suppliers, brokers and services.',
    sections: [
      {
        title: 'Add a company',
        steps: [
          'Open Entity and click Add Entity.',
          'Pick the type at the top. Customer and Supplier go together.',
          'Only Company Name is needed. Add phone, owner, address and trading terms if you have them.',
          'Save.',
        ],
        points: ['You can also add one while making a sale or purchase with + Create New Entity.'],
      },
      {
        title: 'Edit, disable or delete',
        steps: ['Click the company to open it.', 'Use the pencil to edit. The ⋮ menu has Disable / Enable and Delete.'],
      },
      {
        title: 'Good to know',
        points: [
          'Default Keep(%) and Default Terms fill in on new sales for that customer.',
          'Import and Export in the header work with Excel. A name that already exists is skipped.',
          'View history in the company popup opens Entity History.',
        ],
      },
    ],
  },
  {
    key: 'entity-history', tab: 'companies', title: 'Entity History', page: 'entity-history', path: '/entity/history',
    intro: 'Everything done with one company in one list: sales, purchases, payments, returns and memos.',
    sections: [
      {
        title: 'See a company\'s history',
        steps: [
          'Pick the company at the top.',
          'Choose the period. It starts on 3 Months.',
          'Narrow it with Type, search or the column funnels.',
        ],
      },
      {
        title: 'Good to know',
        points: [
          'The figures on top show what they owe you and what you owe them now.',
          'Click an invoice number to open the invoice.',
          'Memo Out: open shows the items they have on memo.',
        ],
      },
    ],
  },

  // ── Accounting ─────────────────────────────────────────────────────────
  {
    key: 'profit', tab: 'accounting', title: 'Profit & Loss', page: 'profit', path: '/profit',
    intro: 'What you earned and spent in a period, and the profit left.',
    sections: [
      {
        title: 'Read it',
        points: [
          'Pick the dates in the header.',
          'Click an account line to see every entry behind it in the General Ledger.',
        ],
      },
    ],
  },
  {
    key: 'balance-sheet', tab: 'accounting', title: 'Balance Sheet', page: 'balance-sheet', path: '/accounting/balance-sheet',
    intro: 'What the business owns and owes on one day.',
    sections: [
      { title: 'Read it', points: ['Pick the As of date in the header.'] },
    ],
  },
  {
    key: 'expenses', tab: 'accounting', title: 'Expenses', page: 'expenses', path: '/accounting/expenses',
    intro: 'Business costs not tied to a sale or purchase, like rent or salaries.',
    sections: [
      {
        title: 'Add an expense',
        steps: [
          'Click Add Expense.',
          'Fill the category, date, amount and who you paid.',
          'Choose the account if paid now, or a due date if you pay later. Save.',
        ],
      },
      {
        title: 'Repeat every month',
        points: ['Recurring › + Add Recurring Expense sets up one that comes back by itself.'],
      },
      {
        title: 'Change or delete',
        points: ['Edit on the row changes it. Delete it with the red bin. Calendar view shows what is due on each day.'],
      },
    ],
  },
  {
    key: 'income', tab: 'accounting', title: 'Income', page: 'income', path: '/accounting/income',
    intro: 'Money that came in that is not a sale, like interest or rent received.',
    sections: [
      {
        title: 'Record income',
        steps: ['Click Record Income.', 'Fill the category, date, amount and account. Save.'],
        points: ['Delete it with the red bin on the row.'],
      },
    ],
  },
  {
    key: 'coa', tab: 'accounting', title: 'Chart of Accounts', page: 'coa', path: '/accounting/coa',
    intro: 'The list of accounts the books use. Owner only.',
    sections: [
      {
        title: 'Set it up',
        steps: ['Go to Settings › Accounting.', 'Click Set Up Now the first time. Later, Repair / Add Missing adds anything new.'],
      },
      { title: 'Good to know', points: ['Search by account name or code.'] },
    ],
  },
  {
    key: 'journal', tab: 'accounting', title: 'Journal Entries', page: 'journal', path: '/accounting/journal',
    intro: 'Every booking in the books. Most are made by themselves. Owner only.',
    sections: [
      {
        title: 'Add your own entry',
        steps: [
          'Click New Entry.',
          'Use + Add line for each account. Debits and credits must be equal.',
          'Save.',
        ],
        points: ['Use Edit or the red bin on your own entries.'],
      },
    ],
  },
  {
    key: 'general-ledger', tab: 'accounting', title: 'General Ledger', page: 'general-ledger', path: '/accounting/ledger',
    intro: 'Every entry on one account, with the running balance. Owner only.',
    sections: [
      { title: 'Read it', points: ['Pick the account and the dates in the header.'] },
    ],
  },
  {
    key: 'trial-balance', tab: 'accounting', title: 'Trial Balance', page: 'trial-balance', path: '/accounting/trial-balance',
    intro: 'All accounts with their debit and credit totals. Owner only.',
    sections: [
      { title: 'Read it', points: ['Balanced means debits and credits are equal, as they should be.'] },
    ],
  },
  {
    key: 'rebuild', tab: 'accounting', title: 'Rebuild the Books', page: 'coa', path: '/settings',
    intro: 'Make the books again from all your sales, purchases and payments. Owner only.',
    sections: [
      {
        title: 'Rebuild',
        steps: [
          'Go to Settings › Accounting › Rebuild the Books.',
          'Optionally set Only from a date, then click Rebuild Now.',
        ],
        points: ['Use it when figures look wrong, for example after a big import.'],
      },
    ],
  },

  // ── GST (Indian companies) ─────────────────────────────────────────────
  {
    key: 'gst-invoices', tab: 'gst', title: 'GST on Invoices', page: 'sales', path: '/sales', indianOnly: true,
    intro: 'GST on sales and purchases, worked out once per invoice.',
    sections: [
      {
        title: 'How it works',
        points: [
          'Set your GST Rate %, Our State and GSTIN in Settings › Custom Options › GST.',
          'On a sale or purchase the GST boxes sit under the items. CGST + SGST for your own state, IGST for another state.',
          'GST is on the items only, never on shipping or other charges.',
          'Less 1 % and Less 2 % on each line give the final price. GST and brokerage use that price.',
        ],
      },
      {
        title: 'Returns',
        points: ['A return takes GST back at the same % as its invoice.'],
      },
    ],
  },
  {
    key: 'gst-pay', tab: 'gst', title: 'Pay GST', page: 'bank', path: '/bank', indianOnly: true,
    intro: 'See how much GST you owe and pay it.',
    sections: [
      {
        title: 'Check and pay',
        steps: [
          'See Reports › Tax › GST Summary for each month.',
          'To pay, open Bank › New › Pay GST.',
          'Input GST is set off first. Pick the account for the rest and save.',
        ],
      },
    ],
  },
];
